import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Spinner } from "../components/ui/spinner";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import axios from "axios";
import { API_BASE_URL } from "../lib/constants";
import { JWT_COOKIE_NAME } from "../lib/cookie";
import Cookie from "../lib/cookie";
import { useTranslation } from "../contexts/I18nProvider";

// Define validation schema using zod
const createLoginSchema = (t) => z.object({
  email: z
    .string()
    .email(t('auth.loginPage.emailInvalid'))
    .min(1, t('auth.loginPage.emailRequired')),
  password: z
    .string()
    .min(6, t('auth.loginPage.passwordTooShort'))
    .min(1, t('auth.loginPage.passwordRequired')),
});

export function LoginForm({ className }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formErrors, setFormErrors] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const loginSchema = createLoginSchema(t);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    setFormErrors({ email: "", password: "" });

    const result = loginSchema.safeParse({ email, password });

    if (!result.success) {
      const errors = result.error.format();
      setFormErrors({
        email: errors.email ? errors.email._errors[0] : "",
        password: errors.password ? errors.password._errors[0] : "",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(API_BASE_URL + "/api/auth/login", {
        email,
        password,
      });
      setSuccess(t('auth.loginPage.successMessage'));

      if (response.status === 200) {
        Cookie.set(JWT_COOKIE_NAME, response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        setTimeout(() => {
          navigate("/");
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      setError(t('auth.loginPage.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={cn(
      className, 
      "w-full max-w-md mx-auto shadow-lg border border-gray-200 dark:border-gray-700"
    )}>
      <CardHeader className="space-y-2 bg-gradient-to-br from-indigo-50/50 to-white dark:from-indigo-900/20 dark:to-gray-900 rounded-t-lg">
        <CardTitle className="text-2xl font-bold text-center text-gray-900 dark:text-white">
          Welcome back
        </CardTitle>
        <CardDescription className="text-center text-gray-600 dark:text-gray-400">
          Enter your email below to login to your account
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Forgot Password Link - Center */}
          <div className="flex justify-center mb-4">
            <a
              href="/forgot-password"
              className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium transition-colors"
            >
              {t('auth.loginPage.forgotPasswordLink')}
            </a>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label 
              htmlFor="email" 
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              {t('auth.loginPage.emailLabel')}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={t('auth.loginPage.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              className={cn(
                "rounded-lg border-gray-200 dark:border-gray-700",
                formErrors.email && "border-red-500 focus:ring-red-500"
              )}
              required
            />
            {formErrors.email && (
              <p className="text-red-500 text-sm font-medium flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {formErrors.email}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label 
              htmlFor="password"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              {t('auth.loginPage.passwordLabel')}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={cn(
                "rounded-lg border-gray-200 dark:border-gray-700",
                formErrors.password && "border-red-500 focus:ring-red-500"
              )}
              required
            />
            {formErrors.password && (
              <p className="text-red-500 text-sm font-medium flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {formErrors.password}
              </p>
            )}
          </div>

          {/* Alerts */}
          {error && (
            <Alert variant="destructive" className="rounded-lg border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="rounded-lg border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle className="text-green-800 dark:text-green-300">Success</AlertTitle>
              <AlertDescription className="text-green-700 dark:text-green-200">{success}</AlertDescription>
            </Alert>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-semibold py-2.5 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Spinner className="w-4 h-4" />
                {t('auth.loginPage.loginButton')}...
              </div>
            ) : (
              t('auth.loginPage.loginButton')
            )}
          </Button>

          {/* Sign Up Link */}
          <div className="pt-4 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.loginPage.noAccountText')}{" "}
              <a
                href="/get-started?tab=signup"
                className="font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
              >
                {t('auth.loginPage.signupLink')}
              </a>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
